import { useState } from "react";
import { useLocation, useSearchParams, Link } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { resetPassword } from "@/lib/auth";
import logo from "@/assets/Logo.png";
import bgDecorative from "@/assets/bg-decorative.svg";

const resetPasswordSchema = z
  .object({
    password: z.string().min(6, "Password must be at least 6 characters"),
    confirmPassword: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

type ResetPasswordValues = z.infer<typeof resetPasswordSchema>;

const inputClassName =
  "h-auto rounded-2xl border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 shadow-none placeholder:text-slate-400 focus-visible:ring-1 focus-visible:ring-slate-300";
const labelClassName = "text-sm font-semibold leading-5 text-slate-700";

function ResetPassword() {
  const [, setLocation] = useLocation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm<ResetPasswordValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { password: "", confirmPassword: "" },
  });

  async function onSubmit(values: ResetPasswordValues) {
    if (!token) {
      return;
    }
    setServerError(null);

    try {
      await resetPassword({ token, newPassword: values.password });
      setLocation("/login");
    } catch (err) {
      setServerError(err instanceof Error ? err.message : "Something went wrong");
    }
  }

  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center px-4 py-8"
      style={{
        backgroundImage: `url(${bgDecorative})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="w-full max-w-[560px] overflow-hidden rounded-3xl border border-slate-200 bg-card shadow-xl">
        <div className="flex flex-col items-center gap-8 p-16">
          <img className="h-9 w-24 object-contain" src={logo} alt="180Pi" />

          {!token ? (
            <>
              <div className="flex w-full flex-col items-center gap-2">
                <h1 className="text-center text-3xl font-extrabold leading-9 text-primary">Invalid reset link</h1>
                <p className="text-center text-base font-normal leading-6 text-muted-foreground">
                  This password reset link is missing or invalid. Request a new one to continue.
                </p>
              </div>
              <Link href="/forgot-password" className="text-sm text-primary underline-offset-4 hover:underline">
                Request a new link
              </Link>
            </>
          ) : (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="flex w-full flex-col items-center gap-8">
                <div className="flex w-full flex-col items-center gap-2">
                  <h1 className="text-center text-3xl font-extrabold leading-9 text-primary">Reset your password</h1>
                  <p className="text-center text-base font-normal leading-6 text-muted-foreground">
                    Choose a new password for your account
                  </p>
                </div>

                <div className="flex w-full flex-col gap-5">
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className={labelClassName}>New password</FormLabel>
                        <FormControl>
                          <Input
                            type="password"
                            autoComplete="new-password"
                            placeholder="••••••••"
                            className={inputClassName}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className={labelClassName}>Confirm new password</FormLabel>
                        <FormControl>
                          <Input
                            type="password"
                            autoComplete="new-password"
                            placeholder="••••••••"
                            className={inputClassName}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {serverError && <p className="text-sm font-medium text-destructive">{serverError}</p>}

                  <Button
                    type="submit"
                    disabled={form.formState.isSubmitting}
                    className="w-full rounded-2xl py-3 text-sm font-bold shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]"
                  >
                    {form.formState.isSubmitting ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      "Reset password"
                    )}
                  </Button>
                </div>
              </form>
            </Form>
          )}
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
